import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { Button } from '../components/ui/button';
import { 
  Package, FileText, ShoppingCart, Award, Users, Shield // Missing imports
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';


function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await apiClient.get(`/products/featured`);
      setFeaturedProducts(response.data);
    } catch (error) {
      console.error('Error fetching featured products:', error);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Welcome to <span className="text-yellow-400">CRESCENT TRADERS</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto">
            Your trusted partner in quality products and exceptional service. Building lasting relationships through reliability, integrity, and excellence.
          </p>
          <div className="space-x-4">
            <Link to="/catalogue">
              <Button size="lg" className="bg-yellow-400 text-slate-900 hover:bg-yellow-300 text-lg px-8 py-3 font-semibold">
                <Package className="mr-2" size={20} />
                Explore Catalogue
              </Button>
            </Link>
            <Link to="/quote">
              <Button size="lg" variant="outline" className="border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-slate-900 text-lg px-8 py-3 font-semibold">
                <FileText className="mr-2" size={20} />
                Get Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products Carousel */}
      {featuredProducts.length > 0 && (
        <section className="py-16 bg-gradient-to-r from-slate-50 to-orange-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12 text-slate-800">Featured Products</h2>
            <Carousel className="max-w-6xl mx-auto">
              <CarouselContent>
                {featuredProducts.map((product) => (
                  <CarouselItem key={product.product_id} className="md:basis-1/2 lg:basis-1/3">
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="aspect-square bg-gradient-to-br from-orange-100 to-amber-100 rounded-lg mb-4 overflow-hidden">
                          {product.image_url ? (
                            <img 
                              src={product.image_url?.startsWith('http') ? product.image_url : `/api${product.image_url}`}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ShoppingCart size={48} className="text-orange-300" />
                            </div>
                          )}
                        </div>
                        <Badge className="bg-orange-100 text-orange-800 mb-2">{product.brand}</Badge>
                        <h3 className="font-semibold text-lg mb-2">{product.name}</h3>
                        <p className="text-slate-600 text-sm mb-3">{product.short_description}</p>
                        <p className="text-2xl font-bold text-orange-600">₹{product.price}</p>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-slate-800">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-amber-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-3">Quality Products</h3>
                <p className="text-slate-600">We source only the highest quality products from trusted manufacturers.</p>
              </CardContent>
            </Card>
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-amber-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-3">Expert Service</h3>
                <p className="text-slate-600">Our experienced team provides personalized service and expert advice.</p>
              </CardContent>
            </Card>
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-amber-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-3">Trusted Partner</h3>
                <p className="text-slate-600">Build lasting relationships with our reliable and trustworthy service.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;